"use client";

import { ChatServiceNameEnum } from "@finbackoffice/fe-core";
import {
    useRuntimeConfig,
    useDidUpdateEffect,
    ChatServiceContext,
    useSearchParamsQuery,
    AuthContext,
} from "@finbackoffice/site-core";
import { UserAccountContextV2 } from "contexts";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { FC, useMemo, useContext, useCallback, useEffect } from "react";
import { RouteNames } from "utils/constants";

export interface IVisibilityState {
    visibility: "maximized" | "minimized" | "hidden";
}

const ChatScript: FC = () => {
    const pathname = usePathname();
    const { query } = useSearchParamsQuery();
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { username, userId, userEmail } = useContext(UserAccountContextV2);
    const { isUserLoggedIn } = useContext(AuthContext);
    const { showWidget, hideWidget, syncVisibility, setConversationFields, clearSession } =
        useContext(ChatServiceContext);
    const hideChatServiceBadge = useMemo(
        () =>
            (pathname?.includes(RouteNames.CASINO) && query?.gameId) ||
            (isUserLoggedIn && !COMMON_SITE_CONFIGS.chatService.showAfterSignIn),
        [isUserLoggedIn, pathname, query?.gameId, COMMON_SITE_CONFIGS.chatService],
    );

    useDidUpdateEffect(() => {
        updateVisibility();
    }, [pathname, isUserLoggedIn]);

    useDidUpdateEffect(() => {
        if (isUserLoggedIn) {
            setConversationFields(username, userId, userEmail || "");
        } else {
            clearSession();
        }
    }, [isUserLoggedIn]);

    const updateVisibility = useCallback(() => {
        if (hideChatServiceBadge) {
            hideWidget();
        } else {
            showWidget();
        }
    }, [hideChatServiceBadge, hideWidget, showWidget]);

    const onLiveChatServiceReady = useCallback(
        (data: { state: IVisibilityState }) => {
            /* if (isUserLoggedIn) {
                setConversationFields(username);
            } */
            syncVisibility(data.state.visibility === "minimized");
        },
        [syncVisibility],
    );

    const onVisibilityChanged = useCallback(
        (data: IVisibilityState) => {
            syncVisibility(data.visibility === "minimized");
        },
        [syncVisibility],
    );

    const onLiveChatScriptLoad = useCallback(() => {
        LiveChatWidget.once("ready", onLiveChatServiceReady);
        LiveChatWidget.on("visibility_changed", onVisibilityChanged);
        updateVisibility();
    }, [onLiveChatServiceReady, onVisibilityChanged, updateVisibility]);

    useEffect(() => {
        return () => {
            if (window.LiveChatWidget && LiveChatWidget._h) {
                LiveChatWidget.off("visibility_changed", onVisibilityChanged);
            }
        };
    }, [onVisibilityChanged]);

    const onZendeskScriptLoad = useCallback(() => {
        updateVisibility();
        if (isUserLoggedIn) {
            setConversationFields(username, userId, userEmail || "");
        }
    }, [isUserLoggedIn, setConversationFields, updateVisibility, userEmail, userId, username]);

    const onTawkScriptLoad = useCallback(() => {
        window.Tawk_API = window.Tawk_API || {};
        if (hideChatServiceBadge) {
            window.Tawk_API.autoStart = false;
        } else {
            window.Tawk_API.autoStart = true;
        }

        window.Tawk_API.onChatHidden = () => {
            syncVisibility(false);
        };

        window.Tawk_API.onChatMinimized = () => {
            syncVisibility(!window.Tawk_API.isChatHidden());
        };

        window.Tawk_API.onLoad = () => {
            syncVisibility(!window.Tawk_API.isChatHidden());
            updateVisibility();
        };
    }, [hideChatServiceBadge, syncVisibility, updateVisibility]);

    const renderChatScript = useMemo(() => {
        let content;
        switch (COMMON_SITE_CONFIGS.chatService.name) {
            case ChatServiceNameEnum.LiveChat:
                content = (
                    <Script
                        src="/assets/js/livechat.js"
                        id={COMMON_SITE_CONFIGS.chatService.id}
                        onLoad={onLiveChatScriptLoad}
                        async
                    />
                );
                break;
            case ChatServiceNameEnum.Zendesk:
                content = (
                    <>
                        <Script
                            onLoad={onZendeskScriptLoad}
                            id="ze-snippet"
                            async
                            src={`https://static.zdassets.com/ekr/snippet.js?key=${COMMON_SITE_CONFIGS.chatService.id}`}
                        />
                        <Script
                            id="zendesk-custom-script"
                            strategy="afterInteractive"
                            dangerouslySetInnerHTML={{
                                __html: `
                                window.zESettings = {
                                    webWidget: {
                                        offset: {
                                            mobile: {
                                                horizontal: '0px',
                                                vertical: '55px'
                                            }

                                        }

                                    }
                                };
                            `,
                            }}
                        />
                    </>
                );
                break;
            case ChatServiceNameEnum.Tawk:
                content = (
                    <Script
                        async
                        src={`https://embed.tawk.to/${COMMON_SITE_CONFIGS.chatService.id}`}
                        onLoad={onTawkScriptLoad}
                    />
                );
                break;
            default:
                content = null;
        }

        return !!COMMON_SITE_CONFIGS.chatService.id && content;
    }, [
        COMMON_SITE_CONFIGS.chatService.name,
        COMMON_SITE_CONFIGS.chatService.id,
        onLiveChatScriptLoad,
        onZendeskScriptLoad,
        onTawkScriptLoad,
    ]);

    return renderChatScript;
};

export default ChatScript;
